import React from "react";
import { IconButton, MessageBar, MessageBarType, Pivot, PivotItem, PrimaryButton, Stack, TextField } from "@fluentui/react";
import useAxios from "axios-hooks";
import Toast from "../../../../Forms/Toast";
import { axiosInstance } from '../../../../../api/config';
import Select from "react-select";
import FeatureSummary from "../../../../Util/FeatureSummary";
import env from "../../../../../env";

const { REACT_APP_API_BASE_URI } = env;
const createInitialFormState = () => ({
    email: "",
    cobrandId: null,
    result: null,
    error: null,
});

const buildIgradLink = (data) => {
    if (!data) {
        return null;
    }

    const resetHash = (data.resetHash || "").trim();
    const cobrandName = (data.cobrandName || "").trim();
    if (!resetHash || !cobrandName) {
        return null;
    }
    return `https://sts.igrad.com/Account/Password/Change?s=${resetHash}`;
};

const buildReportConsoleLink = (data) => {
    if (!data) {
        return null;
    }

    const resetHash = (data.resetHash || "").trim();
    if (!resetHash) {
        return null;
    }

    const host = data.enrichCobrand ? "reports.enrich.org" : "schools.igrad.com";

    return `http://${host}/support/resetpassword.aspx?s=${resetHash}`;
};

const PasswordResetTool = () => {
    const [activeTab, setActiveTab] = React.useState("igrad");
    const [igradForm, setIgradForm] = React.useState(createInitialFormState);
    const [reportConsoleForm, setReportConsoleForm] = React.useState(createInitialFormState);
    const [showToast, setShowToast] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState("");

    const [{ data: cobrands, loading: loadingCobrands }] = useAxios({ url: "/internal/cobrands" });
    const cobrandOptions = React.useMemo(
        () => (cobrands || []).map((c) => ({ value: c.cobrandId, label: c.name })),
        [cobrands]
    );

    const [{ loading: submitting }, generateResetLink] = useAxios(
        {
            url: `2020-05-05/user/password-reset-link`,
            method: "POST",
            baseURL: REACT_APP_API_BASE_URI,
        },
        { axios: axiosInstance, manual: true }
    );

    const getFormState = (key) => (key === "igrad" ? igradForm : reportConsoleForm);

    const setFormState = (key, updater) => {
        if (key === "igrad") {
            setIgradForm((prev) => ({ ...prev, ...updater }));
        } else {
            setReportConsoleForm((prev) => ({ ...prev, ...updater }));
        }
    };

    const handleEmailChange = (key, value) => {
        setFormState(key, { email: value ?? "" });
    };

    const handleCobrandChange = (key, option) => {
        setFormState(key, { cobrandId: option ? option.value : null });
    };

    const handleCopyLink = async (link) => {
        if (!link) {
            setToastMessage("No reset link available to copy.");
            setShowToast(true);
            return;
        }

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(link);
            } else {
                const tempInput = document.createElement("input");
                tempInput.value = link;
                document.body.appendChild(tempInput);
                tempInput.select();
                document.execCommand("copy");
                document.body.removeChild(tempInput);
            }

            setToastMessage("Reset link copied to clipboard.");
            setShowToast(true);
        } catch (error) {
            setToastMessage("Unable to copy reset link.");
            setShowToast(true);
        }
    };

    const extractErrorMessage = (errorResponse) => {
        if (!errorResponse) {
            return "Unable to generate password reset link.";
        }

        const { errors, message, title } = errorResponse;
        if (Array.isArray(errors) && errors.length > 0) {
            return errors[0]?.errorMessage || errors[0]?.ErrorMessage || "Unable to generate password reset link.";
        }

        return message || title || "Unable to generate password reset link.";
    };

    const normalisePayload = (payload) => {
        if (!payload) {
            return { resetHash: "", enrichCobrand: false, cobrandName: "" };
        }

        return {
            resetHash: (payload.resetHash ?? payload.ResetHash ?? "").toString(),
            enrichCobrand: Boolean(payload.enrichCobrand ?? payload.EnrichCobrand),
            cobrandName: (payload.cobrandName ?? payload.CobrandName ?? "").toString(),
            domainMask: (payload.domainMask ?? payload.DomainMask ?? "").toString(),
        };
    };

    const handleSubmit = async (key, isSchoolAdminUser) => {
        const state = getFormState(key);
        const trimmedEmail = state.email ? state.email.trim() : "";

        if (!trimmedEmail || (!isSchoolAdminUser && !state.cobrandId)) {
            setFormState(key, {
                error: !trimmedEmail
                    ? "Please enter an email address."
                    : "Please choose a cobrand for iGrad password resets.",
                result: null,
            });
            return;
        }

        setFormState(key, { error: null, result: null });

        const response = await generateResetLink({
            data: {
                emailAddress: trimmedEmail,
                cobrandId: isSchoolAdminUser ? null : state.cobrandId,
                isSchoolAdminUser,
            },
        });

        if (response.error) {
            const errorMessage = extractErrorMessage(response.error.response?.data);
            setFormState(key, { error: errorMessage, result: null });
            return;
        }

        const normalised = normalisePayload(response.data);
        const trimmedHash = normalised.resetHash.trim();
        if (!trimmedHash) {
            setFormState(key, {
                error: "No password reset data was returned.",
                result: null,
            });
            return;
        }

        const domainMask = (normalised.domainMask || "").trim();
        const normalisedResult = {
            resetHash: trimmedHash,
            enrichCobrand: !!normalised.enrichCobrand,
            cobrandName: normalised.cobrandName.trim(),
            domainMask: domainMask || null,
        };

        const link = isSchoolAdminUser
            ? buildReportConsoleLink(normalisedResult)
            : buildIgradLink({ ...normalisedResult, useDomainMask: !isSchoolAdminUser });

        setFormState(key, {
            result: {
                ...normalisedResult,
                link,
            },
            error: null,
        });
    };

    const renderResult = (state) => {
        if (!state.result) {
            return null;
        }

        const { link, resetHash, cobrandName, enrichCobrand } = state.result;

        return (
            <MessageBar messageBarType={MessageBarType.success} isMultiline style={{ width: "100%" }}>
                <Stack tokens={{ childrenGap: 8 }} style={{ width: "100%" }}>
                    {link && (
                        <Stack tokens={{ childrenGap: 4 }} style={{ width: "100%" }}>
                            <strong>Reset Link</strong>
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" style={{ width: "100%" }}>
                                <TextField
                                    value={link}
                                    readOnly
                                    disabled
                                    ariaLabel="Generated password reset link"
                                    styles={{ root: { flexGrow: 1, width: "100%" }, fieldGroup: { width: "100%" } }}
                                />
                                <IconButton
                                    iconProps={{ iconName: "Copy" }}
                                    title="Copy reset link"
                                    ariaLabel="Copy reset link"
                                    onClick={() => handleCopyLink(link)}
                                />
                            </Stack>
                        </Stack>
                    )}
                    <span>
                        <strong>Reset Hash:</strong> <code>{resetHash}</code>
                    </span>
                    <span>
                        <strong>Cobrand:</strong> {cobrandName || "N/A"}
                    </span>
                    <span>
                        <strong>Enrich Cobrand:</strong> {enrichCobrand ? "Yes" : "No"}
                    </span>
                </Stack>
            </MessageBar>
        );
    };

    const renderError = (state) => {
        if (!state.error) {
            return null;
        }

        return (
            <MessageBar messageBarType={MessageBarType.error} isMultiline>
                {state.error}
            </MessageBar>
        );
    };

    const renderForm = (key, state, isSchoolAdminUser) => {
        const hasEmail = !!(state.email && state.email.trim());
        const buttonDisabled = submitting || !hasEmail || (!isSchoolAdminUser && !state.cobrandId);
        const selectedCobrand = cobrandOptions.find((option) => option.value === state.cobrandId) || null;

        return (
            <Stack tokens={{ childrenGap: 12 }} style={{ width: "100%", maxWidth: 520 }}>
                <TextField
                    label="Email Address"
                    value={state.email}
                    onChange={(_, value) => handleEmailChange(key, value)}
                    autoComplete="off"
                />
                {!isSchoolAdminUser && (
                    <div>
                        <label style={{ display: "block", marginBottom: 4 }}>Cobrand</label>
                        <Select
                            isDisabled={loadingCobrands}
                            isLoading={loadingCobrands}
                            options={cobrandOptions}
                            onChange={(option) => handleCobrandChange(key, option)}
                            value={selectedCobrand}
                            placeholder="Select a cobrand"
                            isSearchable
                            className="mb10"
                        />
                    </div>
                )}
                {renderError(state)}
                {renderResult(state)}
                <PrimaryButton
                    onClick={() => handleSubmit(key, isSchoolAdminUser)}
                    text={submitting ? "Generating..." : "Generate Reset Link"}
                    disabled={buttonDisabled}
                />
            </Stack>
        );
    };

    const handlePivotClick = (item) => {
        if (item?.props?.itemKey) {
            setActiveTab(item.props.itemKey);
        }
    };

    return (
        <div>
            <h2>Password Reset Links</h2>
            <Pivot selectedKey={activeTab} onLinkClick={handlePivotClick}>
                <PivotItem headerText="iGrad" itemKey="igrad">
                    <div style={{ marginTop: 16 }}>{renderForm("igrad", igradForm, false)}</div>
                </PivotItem>
                <PivotItem headerText="Report Console" itemKey="report">
                    <div style={{ marginTop: 16 }}>{renderForm("report", reportConsoleForm, true)}</div>
                </PivotItem>
            </Pivot>

            <Toast show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
            <FeatureSummary
                label="About Password Reset Links"
                summary="Generate password reset links for users. Use the iGrad tab for iGrad users (requires email and cobrand), or the Report Console tab for school admin users (requires email only). Copy the generated link to share with the user."
            />
        </div>
    );
};

export default PasswordResetTool;